import {
  computeBranchRelation,
  computeElementRelation,
} from "../zodiac/chinese/chineseZodiacInteractions.js";
import { animalEmoji, chartSideLabel } from "./coupleMatchVisuals.js";

const PILLAR_KEYS = ["year", "month", "day", "hour"];
const ELEMENTS = ["Wood", "Fire", "Earth", "Metal", "Water"];
const ELEMENT_SLUG = {
  Wood: "wood",
  Fire: "fire",
  Earth: "earth",
  Metal: "metal",
  Water: "water",
};

export const ELEMENT_TIERS = ["deficient", "lean", "balanced", "strong", "excess"];

export function classifyElementTier(combinedCount) {
  const count = combinedCount || 0;
  if (count < 1.5) return "deficient";
  if (count < 2.5) return "lean";
  if (count <= 4) return "balanced";
  if (count <= 5) return "strong";
  return "excess";
}

function interpolate(template, vars = {}) {
  if (!template) return "";
  return Object.entries(vars).reduce((text, [key, value]) => {
    return text.replace(new RegExp(`%\\{${key}\\}`, "g"), String(value ?? ""));
  }, template);
}

export function resolveCoupleMatchLine(t, key, vars = {}, fallbackKey = null) {
  let template = t(key);
  if ((!template || template === key) && fallbackKey) {
    template = t(fallbackKey);
  }
  if (!template || template === key) return "";
  return interpolate(template, vars);
}

function pillarByKey(partners, side, key) {
  return partners?.[side]?.pillars?.find((p) => p.key === key) || null;
}

function scoreBandFromScore(score) {
  if (score >= 65) return "harmony";
  if (score < 40) return "caution";
  return "neutral";
}

function pillarInsightVars(partners, a, b, t) {
  return {
    animalA: a?.animal || "—",
    animalB: b?.animal || "—",
    elementA: a?.element || "—",
    elementB: b?.element || "—",
    partnerA: chartSideLabel(partners, "a", t),
    partnerB: chartSideLabel(partners, "b", t),
  };
}

function matrixCellVars(partners, pillarA, pillarB, cell, t) {
  return {
    ...pillarInsightVars(partners, pillarA, pillarB, t),
    aKey: cell.aKey,
    bKey: cell.bKey,
    roleA: t(`cz_pillar_role_${cell.aKey}`),
    roleB: t(`cz_pillar_role_${cell.bKey}`),
    score: cell.score,
    band: scoreBandFromScore(cell.score),
  };
}

function joinInsightParts(...parts) {
  return parts.filter(Boolean).join(" ");
}

function buildMatrixCellInsight(partners, cell, t) {
  const pillarA = pillarByKey(partners, "a", cell.aKey);
  const pillarB = pillarByKey(partners, "b", cell.bKey);
  const vars = matrixCellVars(partners, pillarA, pillarB, cell, t);

  if (cell.aKey === cell.bKey) {
    const branchInsight = resolveCoupleMatchLine(
      t,
      `couple_match_pillar_insight_${cell.aKey}_${cell.branchRelation}`,
      vars,
      `couple_match_pillar_compare_${cell.branchRelation}`
    );
    const elementInsight = resolveCoupleMatchLine(
      t,
      `couple_match_pillar_element_${cell.aKey}_${cell.elementRelation}`,
      vars,
      `couple_match_element_relation_${cell.elementRelation}`
    );
    return {
      branchInsight,
      elementInsight,
      insightText: joinInsightParts(branchInsight, elementInsight),
    };
  }

  const frame = resolveCoupleMatchLine(t, `couple_match_matrix_frame_${cell.aKey}_${cell.bKey}`, vars);
  const branchLine = resolveCoupleMatchLine(
    t,
    `couple_match_matrix_branch_${cell.branchRelation}`,
    vars
  );
  const elementInsight = resolveCoupleMatchLine(
    t,
    `couple_match_matrix_elements_${cell.elementRelation}`,
    vars
  );
  const branchInsight = joinInsightParts(frame, branchLine);

  return {
    branchInsight,
    elementInsight,
    insightText: joinInsightParts(branchInsight, elementInsight),
  };
}

export function buildPillarInsights(partners, t) {
  const comparisons = PILLAR_KEYS.map((key) => {
    const a = pillarByKey(partners, "a", key);
    const b = pillarByKey(partners, "b", key);
    const branchRelation = computeBranchRelation(a?.animal, b?.animal);
    const elementRelation = computeElementRelation(a?.element, b?.element);
    const vars = pillarInsightVars(partners, a, b, t);
    return {
      key,
      partnerA: a,
      partnerB: b,
      branchRelation,
      elementRelation,
      insightText: resolveCoupleMatchLine(
        t,
        `couple_match_pillar_insight_${key}_${branchRelation}`,
        vars,
        `couple_match_pillar_compare_${branchRelation}`
      ),
      elementText: resolveCoupleMatchLine(
        t,
        `couple_match_pillar_element_${key}_${elementRelation}`,
        vars,
        `couple_match_element_relation_${elementRelation}`
      ),
      branchLabel: t(`couple_match_animal_${branchRelation}`),
      roleA: t(`cz_pillar_role_${key}`),
      desc: t(`cz_pillar_desc_${key}`),
    };
  });

  return {
    intro: resolveCoupleMatchLine(t, "couple_match_pillars_intro"),
    comparisons,
  };
}

export function buildMatrixInsights(matrix, partners, t) {
  const partnerALabel = chartSideLabel(partners, "a", t);
  const partnerBLabel = chartSideLabel(partners, "b", t);

  const enrichedCells = (matrix || []).map((cell) => {
    const pillarA = pillarByKey(partners, "a", cell.aKey);
    const pillarB = pillarByKey(partners, "b", cell.bKey);
    const band = scoreBandFromScore(cell.score);
    const { branchInsight, elementInsight, insightText } = buildMatrixCellInsight(partners, cell, t);
    return {
      ...cell,
      band,
      branchInsight,
      elementInsight,
      insightText,
      label: `${partnerALabel} ${cell.aKey} × ${partnerBLabel} ${cell.bKey}`,
      pillarA,
      pillarB,
      branchLabel: t(`couple_match_animal_${cell.branchRelation}`),
      bandLabel: t(`couple_match_matrix_band_${band}`),
    };
  });

  const sorted = [...enrichedCells].sort((a, b) => b.score - a.score);
  const avg = enrichedCells.length
    ? Math.round(enrichedCells.reduce((s, c) => s + c.score, 0) / enrichedCells.length)
    : 50;
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];

  return {
    intro: resolveCoupleMatchLine(t, "couple_match_matrix_intro"),
    rowsLabel: interpolate(t("couple_match_matrix_rows_label"), { partner: partnerALabel }),
    colsLabel: interpolate(t("couple_match_matrix_cols_label"), { partner: partnerBLabel }),
    summary: interpolate(t("couple_match_matrix_summary"), {
      average: avg,
      strong: strongest ? `${strongest.aKey}/${strongest.bKey} (${strongest.score})` : "—",
      weak: weakest ? `${weakest.aKey}/${weakest.bKey} (${weakest.score})` : "—",
    }),
    cells: enrichedCells,
  };
}

export function buildElementInsights(partners, dayElementRelation, t) {
  const profileA = partners?.a?.elementProfile || {};
  const profileB = partners?.b?.elementProfile || {};
  const combined = {};
  ELEMENTS.forEach((el) => {
    combined[el] = (profileA[el] || 0) + (profileB[el] || 0);
  });

  const nameA = chartSideLabel(partners, "a", t);
  const nameB = chartSideLabel(partners, "b", t);

  const profiles = ELEMENTS.map((el) => {
    const slug = ELEMENT_SLUG[el];
    const count = combined[el] || 0;
    const countA = profileA[el] || 0;
    const countB = profileB[el] || 0;
    const tier = classifyElementTier(count);
    const vars = {
      count: count.toFixed(1),
      countA: countA.toFixed(1),
      countB: countB.toFixed(1),
      element: el,
      nameA,
      nameB,
    };
    const text = resolveCoupleMatchLine(
      t,
      `couple_match_elements_status_${tier}_${slug}`,
      vars,
      tier === "deficient"
        ? `couple_match_elements_gap_${slug}`
        : tier === "excess"
          ? `couple_match_elements_excess_${slug}`
          : null
    );
    return {
      element: el,
      slug,
      count,
      countA,
      countB,
      tier,
      tierLabel: t(`couple_match_elements_tier_${tier}`),
      text,
    };
  });

  return {
    intro: resolveCoupleMatchLine(t, "couple_match_elements_intro"),
    combined,
    profiles,
    chemistryText: resolveCoupleMatchLine(t, `couple_match_element_relation_${dayElementRelation}`),
  };
}

function cycleFlags(cycle, currentYear) {
  const isCurrent = currentYear >= cycle.startYear && currentYear <= cycle.endYear;
  const isUpcoming = !isCurrent && cycle.startYear > currentYear;
  return { isCurrent, isUpcoming };
}

function activeDaYunCycle(cycles, year) {
  return (cycles || []).find((c) => year >= c.startYear && year <= c.endYear) || null;
}

function buildCoupleDaYunWindows(partners, startYear, span = 45) {
  const daYunA = partners?.a?.daYun || [];
  const daYunB = partners?.b?.daYun || [];
  const endYear = startYear + span;
  const windows = [];
  let current = null;

  for (let year = startYear; year <= endYear; year += 1) {
    const cycleA = activeDaYunCycle(daYunA, year);
    const cycleB = activeDaYunCycle(daYunB, year);
    const key = `${cycleA?.index ?? "none"}-${cycleB?.index ?? "none"}`;
    if (!current || current.key !== key) {
      if (current) windows.push(current);
      current = { key, startYear: year, endYear: year, cycleA, cycleB };
    } else {
      current.endYear = year;
    }
  }
  if (current) windows.push(current);
  return windows;
}

function enrichCoupleWindow(window, partners, currentYear, t) {
  const { cycleA, cycleB, startYear, endYear } = window;
  const branchRelation =
    cycleA && cycleB ? computeBranchRelation(cycleA.animal, cycleB.animal) : "neutral";
  const vars = {
    animalA: cycleA?.animal || "—",
    animalB: cycleB?.animal || "—",
    partnerA: chartSideLabel(partners, "a", t),
    partnerB: chartSideLabel(partners, "b", t),
    startYear,
    endYear,
    years: `${startYear}–${endYear}`,
  };
  const isCurrent = currentYear >= startYear && currentYear <= endYear;
  const isUpcoming = startYear > currentYear;

  return {
    ...window,
    branchRelation,
    isCurrent,
    isUpcoming,
    branchLabel: t(`couple_match_animal_${branchRelation}`),
    overlapText: resolveCoupleMatchLine(t, `couple_match_dayun_overlap_${branchRelation}`, vars),
    coupleInsight: resolveCoupleMatchLine(
      t,
      `couple_match_dayun_window_${branchRelation}`,
      vars,
      `couple_match_dayun_overlap_${branchRelation}`
    ),
    partnerA: vars.partnerA,
    partnerB: vars.partnerB,
  };
}

export function buildDaYunInsights(partners, t, { currentYear } = {}) {
  const year = currentYear || new Date().getFullYear();
  const windowStart = Math.max(year - 2, (partners?.a?.daYun?.[0]?.startYear || year));
  const rawWindows = buildCoupleDaYunWindows(partners, windowStart);
  const coupleWindows = rawWindows.map((w) => enrichCoupleWindow(w, partners, year, t));

  const partnerInsights = ["a", "b"].map((side) => {
    const partner = partners[side];
    const cycles = (partner?.daYun || []).map((cycle) => ({
      ...cycle,
      ...cycleFlags(cycle, year),
    }));
    return { side, partner, cycles };
  });

  const footer = resolveCoupleMatchLine(t, "couple_match_dayun_footer");

  return {
    intro: resolveCoupleMatchLine(t, "couple_match_dayun_intro"),
    decadeExplainer: resolveCoupleMatchLine(t, "couple_match_dayun_decade_explainer"),
    coupleWindows,
    partners: partnerInsights,
    footer,
  };
}

export function buildJointYearNarrative(yearItem, t, partners, { locale = "en" } = {}) {
  const animalKey = (yearItem.annualAnimal || "rat").toLowerCase();
  const emoji = animalEmoji(yearItem.annualAnimal, locale);
  const nameA = chartSideLabel(partners, "a", t);
  const nameB = chartSideLabel(partners, "b", t);
  const relA = yearItem.factors?.relA;
  const relB = yearItem.factors?.relB;
  const cycleA = yearItem.factors?.cycleA?.animal;
  const cycleB = yearItem.factors?.cycleB?.animal;

  const vars = {
    year: yearItem.year,
    animal: yearItem.annualAnimal,
    emoji,
    ganZhi: yearItem.ganZhi,
    nameA,
    nameB,
    relA: relA ? t(`couple_match_animal_${relA}`) : "",
    relB: relB ? t(`couple_match_animal_${relB}`) : "",
    cycleA: cycleA || "—",
    cycleB: cycleB || "—",
  };

  const opening = resolveCoupleMatchLine(t, `couple_match_joint_open_${yearItem.tone}`, vars);
  const counsel = resolveCoupleMatchLine(t, `couple_match_joint_counsel_${yearItem.tone}_${animalKey}`, vars);
  const text = [opening, counsel].filter(Boolean).join(" ");

  const whyParts = [];
  if (relA) {
    whyParts.push(
      resolveCoupleMatchLine(t, "couple_match_joint_why_first", {
        name: nameA,
        relation: t(`couple_match_animal_${relA}`),
        animal: yearItem.annualAnimal,
        emoji,
      })
    );
  }
  if (relB) {
    whyParts.push(
      resolveCoupleMatchLine(t, "couple_match_joint_why_second", {
        name: nameB,
        relation: t(`couple_match_animal_${relB}`),
        animal: yearItem.annualAnimal,
        emoji,
      })
    );
  }
  if (cycleA && cycleB) {
    whyParts.push(
      resolveCoupleMatchLine(t, "couple_match_joint_why_cycles", {
        cycleA,
        cycleB,
        emojiA: animalEmoji(cycleA, locale),
        emojiB: animalEmoji(cycleB, locale),
      })
    );
  }

  const headline = resolveCoupleMatchLine(t, `couple_match_joint_headline_${yearItem.tone}`, vars);

  return {
    headline: headline || interpolate(t(`couple_match_joint_tone_${yearItem.tone}`), { year: yearItem.year }),
    text,
    whyText: whyParts.filter(Boolean).join(" "),
    focus: resolveCoupleMatchLine(t, `couple_match_joint_focus_${yearItem.tone}`, vars),
  };
}

export function buildLifeTogetherNarrative({
  tone,
  variant,
  dayRelation,
  yearRelation,
  dayElementRelation,
  t,
}) {
  const v = variant % 8;
  const headline = resolveCoupleMatchLine(t, `couple_match_life_headline_${tone}_${v}`);
  const strengths = [
    resolveCoupleMatchLine(t, `couple_match_life_strength_${tone}_${v}`),
    resolveCoupleMatchLine(t, `couple_match_pillar_compare_${dayRelation}`),
  ].filter(Boolean);
  const watchouts = [
    resolveCoupleMatchLine(t, `couple_match_life_watchout_${tone}_${v}`),
    yearRelation === "liu_chong"
      ? resolveCoupleMatchLine(t, "couple_match_pillar_compare_liu_chong")
      : "",
  ].filter(Boolean);
  const plan = [
    resolveCoupleMatchLine(t, `couple_match_life_plan_${tone}_${v}`),
    resolveCoupleMatchLine(t, `couple_match_element_relation_${dayElementRelation}`),
  ].filter(Boolean);

  const legacyText = resolveCoupleMatchLine(t, `couple_match_life_together_${tone}_${v}`);

  return {
    tone,
    variant: v,
    headline: headline || legacyText,
    strengths,
    watchouts,
    plan,
    text: legacyText || headline,
  };
}

export function buildCoupleMatchNarratives(result, t, { currentYear, locale } = {}) {
  if (!result?.partners) return null;

  const jointYears = (result.joint_timeline || []).map((yearItem) => ({
    ...yearItem,
    narrative: buildJointYearNarrative(yearItem, t, result.partners, { locale }),
  }));

  return {
    pillars: buildPillarInsights(result.partners, t),
    matrix: buildMatrixInsights(result.pillar_matrix, result.partners, t),
    elements: buildElementInsights(
      result.partners,
      result.animal_summary?.dayElementRelation,
      t
    ),
    dayun: buildDaYunInsights(result.partners, t, { currentYear }),
    jointYears,
    lifeTogether: buildLifeTogetherNarrative({
      tone: result.life_together?.tone || "balanced",
      variant: result.life_together?.variant ?? 0,
      dayRelation: result.animal_summary?.dayRelation,
      yearRelation: result.animal_summary?.yearRelation,
      dayElementRelation: result.animal_summary?.dayElementRelation,
      t,
    }),
  };
}
